import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiShoppingCart, FiPlay, FiCheck, FiClock, FiUsers } from 'react-icons/fi';
import { subscriptionService, VideoPackage } from '../services/subscriptionService';
import { cartService } from '../services/cartService';

interface EnrollmentButtonProps {
  item: {
    id: number;
    title?: string;
    name?: string;
    price: number;
    discount_price?: number;
    type: 'book' | 'package';
    image?: string;
    duration_months?: number;
    max_users?: number;
  };
  bookPrice?: number; // For packages, the price of the first book
  userId?: number;
  onEnrollmentSuccess?: (type: 'subscription' | 'cart', item: any) => void;
  addedToCart?: boolean;
  onAddedToCartChange?: (added: boolean) => void;
}

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EnrollmentButton = styled.button<{ $variant: 'primary' | 'package' | 'success' }>`
  flex: 1;
  padding: 12px 16px;
  background: ${props => {
    if (props.$variant === 'success') return '#10b981';
    if (props.$variant === 'package') return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    return '#253C8C';
  }};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  height: 48px;
  min-width: 140px;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background: ${props => {
      if (props.$variant === 'success') return '#059669';
      if (props.$variant === 'package') return 'linear-gradient(135deg, #5a67d8 0%, #667eea 100%)';
      return '#1e40af';
    }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const PackageInfo = styled.div`
  font-size: 11px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
`;

const PriceInfo = styled.div`
  font-size: 12px;
  color: #374151;
  text-align: center;
  margin-top: 4px;
`;

const EnrollmentButtonComponent: React.FC<EnrollmentButtonProps> = ({
  item,
  bookPrice,
  userId,
  onEnrollmentSuccess,
  addedToCart = false,
  onAddedToCartChange
}) => {
  const [availablePackages, setAvailablePackages] = useState<VideoPackage[]>([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Safe price formatter
  const formatPrice = (price: any): string => {
    try {
      const numPrice = Number(price);
      return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
    } catch (error) {
      console.error('Error formatting price:', price, error);
      return '0.00';
    }
  };

  useEffect(() => {
    if (item.type === 'book' && userId) {
      loadPackagesAndAccess();
    }
  }, [item.id, item.type, userId]);

  const loadPackagesAndAccess = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    try {
      // Check for video packages for this book
      const packages = await subscriptionService.getPackagesForBook(item.id);
      setAvailablePackages(packages);

      // Check if user already has access to this book
      const access = await subscriptionService.hasBookAccess(userId, item.id);
      setHasAccess(access);
    } catch (error) {
      console.error('Error loading packages and access:', error);
      setError('Failed to load package information');
      // Set safe defaults
      setAvailablePackages([]);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSubscription = async (packageData: VideoPackage) => {
    if (!userId) {
      alert('Please log in to subscribe to video packages');
      return;
    }

    setIsProcessing(true);
    try {
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = subscriptionService.calculateEndDate(startDate, packageData.duration_months);
      const effectivePrice = subscriptionService.getEffectivePrice(packageData.price, packageData.discount_price);

      const subscriptionData = {
        user_id: userId,
        package_id: packageData.id,
        start_date: startDate,
        end_date: endDate,
        amount_paid: effectivePrice,
        payment_method: 'card' as const,
        txn_reference: `PKG_${packageData.id}_${Date.now()}`
      };

      const subscription = await subscriptionService.createSubscription(subscriptionData);
      
      if (subscription) {
        setHasAccess(true);
        onEnrollmentSuccess?.('subscription', { ...packageData, subscription });
        alert(`Successfully subscribed to ${packageData.name}! You now have access to all videos for this book.`);
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      alert(`Subscription failed: ${error.message || 'Please try again later'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBookPurchase = () => {
    const cartItem = {
      id: item.id,
      title: item.title || item.name || 'Unknown Item',
      price: item.price,
      image: item.image || undefined,
      type: item.type
    };

    const success = cartService.addToCart(cartItem);
    
    if (success) {
      onAddedToCartChange?.(true);
      onEnrollmentSuccess?.('cart', cartItem);
      
      // Auto-open cart
      window.dispatchEvent(new CustomEvent('openCart'));
      
      // Reset feedback after 3 seconds
      setTimeout(() => {
        onAddedToCartChange?.(false);
      }, 3000);
    }
  };

  const handlePackageAddToCart = () => {
    const cartItem = {
      id: item.id,
      title: item.name || item.title || 'Video Package',
      price: item.price,
      image: item.image || undefined,
      type: 'package' as const
    };

    const success = cartService.addToCart(cartItem);
    
    if (success) {
      onAddedToCartChange?.(true);
      onEnrollmentSuccess?.('cart', cartItem);
      
      // Auto-open cart
      window.dispatchEvent(new CustomEvent('openCart'));
      
      // Reset feedback after 3 seconds
      setTimeout(() => {
        onAddedToCartChange?.(false);
      }, 3000);
    }
  };

  // If user already has access
  if (hasAccess) {
    return (
      <ButtonContainer>
        <EnrollmentButton $variant="success" disabled>
          <FiCheck size={16} />
          Access Granted
        </EnrollmentButton>
        <PackageInfo>
          <FiPlay size={12} />
          You have access to this content
        </PackageInfo>
      </ButtonContainer>
    );
  }

  // If there's an error, show fallback button
  if (error) {
    return (
      <EnrollmentButton
        $variant={addedToCart ? 'success' : 'primary'}
        onClick={handleBookPurchase}
      >
        <FiShoppingCart size={16} />
        {addedToCart ? '✓ Added!' : 'Add to Cart'}
      </EnrollmentButton>
    );
  }

  // If loading
  if (loading) {
    return (
      <EnrollmentButton $variant="primary" disabled>
        <FiClock size={16} />
        Loading...
      </EnrollmentButton>
    );
  }

  // For books - always show both video package and book-only options
  if (item.type === 'book') {
    const hasPackages = availablePackages.length > 0;
    const bestPackage = hasPackages ? availablePackages[0] : null;
    
    let packagePrice = 0;
    let packageDiscountPrice: number | undefined;
    let effectivePrice = 0;
    let savings = 0;
    
    if (bestPackage) {
      // Debug: Log package data to see what we're getting
      console.log('📦 Best package data:', bestPackage);
      console.log('💰 Package price from DB:', bestPackage.price);
      console.log('💰 Package discount price:', bestPackage.discount_price);
      
      // Safe price calculations with type checking
      packagePrice = Number(bestPackage.price) || 0;
      packageDiscountPrice = bestPackage.discount_price ? Number(bestPackage.discount_price) : undefined;
      effectivePrice = subscriptionService.getEffectivePrice(packagePrice, packageDiscountPrice);
      savings = packageDiscountPrice ? packagePrice - packageDiscountPrice : 0;
      
      console.log('💰 Calculated effective price:', effectivePrice);
      console.log('💰 Formatted price:', formatPrice(effectivePrice));
    }

    return (
      <ButtonContainer>
        {/* Video Package Button - Always show for books, enable only if linked to package */}
        <EnrollmentButton
          $variant={hasPackages ? "package" : "primary"}
          onClick={hasPackages ? () => handlePackageSubscription(bestPackage!) : undefined}
          disabled={!hasPackages || isProcessing}
          style={{ 
            opacity: hasPackages ? 1 : 0.5,
            cursor: hasPackages ? 'pointer' : 'not-allowed',
            background: hasPackages ? 
              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 
              '#9ca3af'
          }}
        >
          <FiPlay size={16} />
          {isProcessing ? 'Subscribing...' : 
           hasPackages ? `Video Package Only - $${formatPrice(effectivePrice)}` : 
           'Video Package Only - Not Available'}
        </EnrollmentButton>
        
        {/* Package availability info */}
        {!hasPackages && (
          <PackageInfo style={{ color: '#6b7280', fontStyle: 'italic' }}>
            📦 No video package available for this book
          </PackageInfo>
        )}
        
        {hasPackages && bestPackage && (
          <PackageInfo>
            <FiUsers size={12} />
            {bestPackage.duration_months || 12} months access
            {savings > 0 && <span style={{ color: '#10b981', fontWeight: '600' }}> (Save ${formatPrice(savings)})</span>}
          </PackageInfo>
        )}
        
        {hasPackages && (
          <PriceInfo>
            <strong>Includes:</strong> Book + Video Lessons + Practice Materials
          </PriceInfo>
        )}

        {/* Book-only purchase option */}
        <EnrollmentButton
          $variant={addedToCart ? 'success' : 'primary'}
          onClick={handleBookPurchase}
          style={{ marginTop: '8px', height: '36px', fontSize: '12px' }}
        >
          <FiShoppingCart size={14} />
          {addedToCart ? '✓ Book Added!' : `Book Only - $${formatPrice(item.price)}`}
        </EnrollmentButton>
      </ButtonContainer>
    );
  }

  // If it's a package item - always show both package and book options
  if (item.type === 'package') {
    const packagePrice = Number(item.price) || 0;
    const packageDiscountPrice = item.discount_price ? Number(item.discount_price) : undefined;
    // Always show the regular package price from content_packages.price column
    const displayPrice = packagePrice;
    
    // Use the book price passed from parent component
    const firstBookPrice = bookPrice || 0;
    
    return (
      <ButtonContainer>
        <EnrollmentButton
          $variant={addedToCart ? 'success' : 'package'}
          onClick={handlePackageAddToCart}
          disabled={isProcessing}
        >
          <FiShoppingCart size={16} />
          {addedToCart ? '✓ Package Added!' : `Add Package - $${formatPrice(displayPrice)}`}
        </EnrollmentButton>
        
        {/* Always show Book Only option for consistency */}
        <EnrollmentButton
          $variant="primary"
          onClick={handleBookPurchase}
          style={{ marginTop: '8px', height: '36px', fontSize: '12px' }}
        >
          <FiShoppingCart size={14} />
          {`Book Only - $${formatPrice(firstBookPrice)}`}
        </EnrollmentButton>
      </ButtonContainer>
    );
  }

  // Default: Book without video packages
  return (
    <EnrollmentButton
      $variant={addedToCart ? 'success' : 'primary'}
      onClick={handleBookPurchase}
    >
      <FiShoppingCart size={16} />
      {addedToCart ? '✓ Added!' : 'Add to Cart'}
    </EnrollmentButton>
  );
};

export default EnrollmentButtonComponent;
